// Hero equity chart for the Strategy Report. Mirrors TradingView's equity pane with
// toggleable overlays:
//   - Equity (always on)              — green line, base 100
//   - Buy & hold                      — yellow line
//   - Drawdowns                       — red shaded line below
//
// X-axis is real UTC time using BarTimes from the backend, so panning/zooming behaves
// the same as the price chart.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
  LineSeries,
} from "lightweight-charts";
import type { BacktestResponseDto } from "../../api/types";
import { runningMax } from "./backtestMath";

interface Props {
  result: BacktestResponseDto;
  height?: number;
}

type OverlayKind = "buyHold" | "drawdown";

interface CurveSource {
  label: string;
  equityByBar: number[];
  buyHoldByBar: number[];
  barTimes: string[];
}

function pickCurve(result: BacktestResponseDto): CurveSource | null {
  if (result.strategy) {
    return {
      label: "Netting (TradingView)",
      equityByBar: result.strategy.equityByBar,
      buyHoldByBar: result.strategy.buyHoldByBar,
      barTimes: result.strategy.barTimes,
    };
  }
  if (result.merged) {
    return {
      label: "Merged",
      equityByBar: result.merged.equityByBar,
      buyHoldByBar: result.merged.buyHoldByBar,
      barTimes: result.merged.barTimes,
    };
  }
  if (result.long) {
    return { label: "Long", equityByBar: result.long.equityByBar, buyHoldByBar: result.long.buyHoldByBar, barTimes: result.long.barTimes };
  }
  if (result.short) {
    return { label: "Short", equityByBar: result.short.equityByBar, buyHoldByBar: result.short.buyHoldByBar, barTimes: result.short.barTimes };
  }
  return null;
}

// Use the shared UTC-defensive parser (appends Z when a zone designator is missing) so equity
// points don't shift by the viewer's TZ offset on any zone-less timestamp.
import { toChartTime as toUtcChartSeconds } from "../../hooks/time";
const toChartTime = (iso: string): Time => (Math.floor(toUtcChartSeconds(iso)) as unknown as Time);

export function EquityChart({ result, height = 300 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const equitySeries = useRef<ISeriesApi<"Line"> | null>(null);
  const bhSeries = useRef<ISeriesApi<"Line"> | null>(null);
  const drawdownSeries = useRef<ISeriesApi<"Line"> | null>(null);

  const [overlays, setOverlays] = useState<Set<OverlayKind>>(new Set(["buyHold"]));

  const curve = useMemo(() => pickCurve(result), [result]);

  // Mount.
  useEffect(() => {
    if (!containerRef.current) return;
    chartRef.current = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: "#0d1117" }, textColor: "#c9d1d9" },
      grid: { vertLines: { color: "#161b22" }, horzLines: { color: "#161b22" } },
      timeScale: { borderColor: "#30363d", timeVisible: true, secondsVisible: false },
      rightPriceScale: { borderColor: "#30363d" },
      crosshair: { mode: 0 },
    });
    equitySeries.current = chartRef.current.addSeries(LineSeries, {
      color: "#3fb950",
      lineWidth: 2,
      title: "Equity",
    });
    bhSeries.current = chartRef.current.addSeries(LineSeries, {
      color: "#d29922",
      lineWidth: 1,
      title: "Buy & hold",
      visible: false,
    });
    drawdownSeries.current = chartRef.current.addSeries(LineSeries, {
      color: "#f85149",
      lineWidth: 1,
      title: "Drawdown",
      visible: false,
      priceScaleId: "drawdown",
    });
    // Configure the drawdown scale on the LEFT so it doesn't fight the equity scale.
    chartRef.current.priceScale("drawdown").applyOptions({
      scaleMargins: { top: 0.7, bottom: 0 },
      borderColor: "#30363d",
    });
    return () => {
      chartRef.current?.remove();
      chartRef.current = null;
      equitySeries.current = null;
      bhSeries.current = null;
      drawdownSeries.current = null;
    };
  }, []);

  // Push data.
  useEffect(() => {
    if (!equitySeries.current || !bhSeries.current || !drawdownSeries.current) return;
    if (!curve || curve.equityByBar.length === 0) return;

    // Some backends may emit duplicate or unsorted timestamps under malformed data; dedupe + sort.
    const points = curve.equityByBar.map((v, i) => ({
      t: toChartTime(curve.barTimes[i]),
      eq: v,
      bh: curve.buyHoldByBar[i],
    }));
    points.sort((a, b) => (a.t as number) - (b.t as number));
    const dedup: typeof points = [];
    let lastT = -1;
    for (const p of points) {
      if ((p.t as number) === lastT) continue;
      dedup.push(p);
      lastT = p.t as number;
    }

    equitySeries.current.setData(dedup.map((p) => ({ time: p.t, value: p.eq })));
    bhSeries.current.setData(dedup.map((p) => ({ time: p.t, value: p.bh })));

    // Drawdown curve from equity: (equity - runningPeak) / runningPeak * 100 (negative).
    const eqArr = dedup.map((p) => p.eq);
    const peaks = runningMax(eqArr);
    const ddPoints = dedup.map((p, i) => ({
      time: p.t,
      value: peaks[i] === 0 ? 0 : ((p.eq - peaks[i]) / peaks[i]) * 100,
    }));
    drawdownSeries.current.setData(ddPoints);

    chartRef.current?.timeScale().fitContent();
  }, [curve]);

  // Apply overlay visibility.
  useEffect(() => {
    bhSeries.current?.applyOptions({ visible: overlays.has("buyHold") });
    drawdownSeries.current?.applyOptions({ visible: overlays.has("drawdown") });
  }, [overlays]);

  if (!curve) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No equity data — run a backtest first.
      </div>
    );
  }

  const toggle = (k: OverlayKind) => {
    setOverlays((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Equity chart <span style={{ color: "#8b949e", fontWeight: 400, fontSize: 11 }}>({curve.label})</span></div>
        <div style={{ display: "flex", gap: 6, fontSize: 11 }}>
          <ToggleChip label="Equity" color="#3fb950" active checked disabled />
          <ToggleChip label="Buy & hold" color="#d29922" active={overlays.has("buyHold")} checked={overlays.has("buyHold")} onClick={() => toggle("buyHold")} />
          <ToggleChip label="Drawdowns" color="#f85149" active={overlays.has("drawdown")} checked={overlays.has("drawdown")} onClick={() => toggle("drawdown")} />
        </div>
      </div>
      <div ref={containerRef} style={{ height, border: "1px solid #30363d", borderRadius: 4 }} />
    </div>
  );
}

function ToggleChip({
  label, color, active, checked, onClick, disabled,
}: { label: string; color: string; active: boolean; checked: boolean; onClick?: () => void; disabled?: boolean }) {
  return (
    <span
      onClick={disabled ? undefined : onClick}
      style={{
        padding: "2px 8px",
        borderRadius: 12,
        border: `1px solid ${active ? color : "#30363d"}`,
        background: active ? `${color}22` : "transparent",
        color: active ? color : "#8b949e",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        userSelect: "none",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 8, background: color, display: "inline-block" }} />
      {checked ? "✓ " : ""}{label}
    </span>
  );
}
